import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { Tooltip } from '@instructure/ui-overlays';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { omitProps } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'bZMhq',
  template: function template(theme) {
    return "\n\n.bZMhq_bGBk{align-items:center;background:".concat(theme.background || 'inherit', ";border-radius:").concat(theme.borderRadius || 'inherit', ";border-style:").concat(theme.borderStyle || 'inherit', ";border-width:").concat(theme.borderWidth || 'inherit', ";box-sizing:border-box;font-family:").concat(theme.fontFamily || 'inherit', ";height:").concat(theme.height || 'inherit', ";padding:").concat(theme.padding || 'inherit', "}\n\n.bZMhq_bGBk.bZMhq_daKB{outline:none;overflow:visible;position:relative}\n\n.bZMhq_bGBk.bZMhq_daKB:before{border:").concat(theme.focusOutlineWidth || 'inherit', " ").concat(theme.focusOutlineStyle || 'inherit', " ").concat(theme.focusOutlineColor || 'inherit', ";border-radius:").concat(theme.borderRadius || 'inherit', ";bottom:-0.375rem;box-sizing:border-box;content:\"\";display:block;left:-0.375rem;opacity:0;pointer-events:none;position:absolute;right:-0.375rem;top:-0.375rem;transform:scale(0.95);transition:all 0.2s}\n\n.bZMhq_bGBk.bZMhq_daKB.bZMhq_cVYB:before{opacity:1;transform:scale(1)}\n\n.bZMhq_eWKC{box-sizing:border-box;flex:1 0 auto;font-size:").concat(theme.textFontSize || 'inherit', ";font-weight:").concat(theme.textFontWeight || 'inherit', ";letter-spacing:0.0625rem;line-height:").concat(theme.lineHeight || 'inherit', ";max-width:").concat(theme.maxWidth || 'inherit', ";min-width:0.0625rem;text-align:center;text-transform:").concat(theme.textTransformStyle || 'inherit', "}\n\n[dir=ltr] .bZMhq_eWKC,[dir=rtl] .bZMhq_eWKC{text-align:center}\n\n.bZMhq_ddES{border-color:").concat(theme.color || 'inherit', "}\n\n.bZMhq_ddES .bZMhq_eWKC{color:").concat(theme.color || 'inherit', "}\n\n.bZMhq_bXiG{border-color:").concat(theme.primaryColor || 'inherit', "}\n\n.bZMhq_bXiG .bZMhq_eWKC{color:").concat(theme.primaryColor || 'inherit', "}\n\n.bZMhq_cOXX{border-color:").concat(theme.successColor || 'inherit', "}\n\n.bZMhq_cOXX .bZMhq_eWKC{color:").concat(theme.successColor || 'inherit', "}\n\n.bZMhq_zGXc{border-color:").concat(theme.dangerColor || 'inherit', "}\n\n.bZMhq_zGXc .bZMhq_eWKC{color:").concat(theme.dangerColor || 'inherit', "}\n\n.bZMhq_eRqw{border-color:").concat(theme.warningColor || 'inherit', "}\n\n.bZMhq_eRqw .bZMhq_eWKC{color:").concat(theme.warningColor || 'inherit', "}\n\n.bZMhq_elxg{border-color:").concat(theme.messageColor || 'inherit', "}\n\n.bZMhq_elxg .bZMhq_eWKC{color:").concat(theme.messageColor || 'inherit', "}");
  },
  'root': 'bZMhq_bGBk',
  'truncated': 'bZMhq_daKB',
  'focused': 'bZMhq_cVYB',
  'text': 'bZMhq_eWKC',
  'default': 'bZMhq_ddES',
  'primary': 'bZMhq_bXiG',
  'success': 'bZMhq_cOXX',
  'danger': 'bZMhq_zGXc',
  'warning': 'bZMhq_eRqw',
  'message': 'bZMhq_elxg'
};
import theme from './theme';
import { TruncateText } from '../TruncateText';
/**
---
category: components/deprecated
id: DeprecatedPill
---
**/

var Pill = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(Pill, _Component);

  function Pill(props) {
    var _this;

    _classCallCheck(this, Pill);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Pill).call(this, props));
    _this.state = {
      truncated: false
    };
    return _this;
  }

  _createClass(Pill, [{
    key: "handleTruncation",
    value: function handleTruncation(truncated) {
      this.setState({
        truncated: truncated
      });
    }
  }, {
    key: "renderPill",
    value: function renderPill(focused, getTriggerProps) {
      var _classnames,
          _this2 = this;

      var _this$props = this.props,
          margin = _this$props.margin,
          text = _this$props.text,
          variant = _this$props.variant,
          as = _this$props.as,
          elementRef = _this$props.elementRef;
      var passthroughProps = View.omitViewProps(omitProps(this.props, Pill.propTypes), Pill);
      var props = typeof getTriggerProps === 'function' ? getTriggerProps(passthroughProps) : passthroughProps;
      var classes = classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles.truncated, this.state.truncated), _defineProperty(_classnames, styles.focused, focused), _defineProperty(_classnames, styles[variant], variant), _classnames));
      return React.createElement(View, Object.assign({}, props, {
        className: classes,
        as: as,
        elementRef: elementRef,
        margin: margin,
        display: "inline-flex",
        __dangerouslyIgnoreExperimentalWarnings: true
      }), React.createElement("span", {
        className: styles.text
      }, React.createElement(TruncateText, {
        onUpdate: function onUpdate(truncated) {
          _this2.handleTruncation(truncated);
        }
      }, text)));
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      if (this.state.truncated) {
        return React.createElement(Tooltip, {
          tip: this.props.text
        }, function (_ref) {
          var focused = _ref.focused,
              getTriggerProps = _ref.getTriggerProps;
          return _this3.renderPill(focused, getTriggerProps);
        });
      } else {
        return this.renderPill();
      }
    }
  }]);

  Pill.displayName = "Pill";
  return Pill;
}(Component), _class2.propTypes = {
  text: PropTypes.node.isRequired,

  /**
  * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing,
  variant: PropTypes.oneOf(['default', 'success', 'danger', 'primary', 'warning', 'message']),
  elementRef: PropTypes.func,
  as: PropTypes.elementType // eslint-disable-line react/require-default-props

}, _class2.defaultProps = {
  margin: void 0,
  elementRef: void 0,
  variant: 'default'
}, _temp)) || _class) || _class);
export default Pill;
export { Pill };